import { useEffect, FunctionComponent, ChangeEvent, JSXElementConstructor, ComponentType } from 'react'
import { RESPONSE_PENDING } from 'libraries/front-end-core/constants/responses'
import { noop, isEmpty } from 'libraries/lodash'
import cx from 'classnames'
import ResponseConsumer from 'components/shared/ResponseConsumer'
import useClickEvents from 'hooks/useClickEvents'
import _QaAccordionNotFoundContainer from 'redux/containers/components/accordion/QaAccordionNotFoundContainer'
import _AccordionFoundContainer from 'redux/containers/components/accordion/AccordionFoundContainer'
import { AccordionWrapperContainerProps } from 'redux/containers/components/accordion/AccordionWrapperContainer'
import { AccordionState } from 'redux/reducers/accordion'
import { ExpandCallback, SelectCallback } from 'interfaces/accordion'

const QaAccordionNotFoundContainer = _QaAccordionNotFoundContainer as ComponentType<any>
const AccordionFoundContainer = _AccordionFoundContainer as ComponentType<any>

const offsetParent = (nodeName: string, element: HTMLElement | null): HTMLElement | null =>
  element === null || element.nodeName.toLowerCase() === nodeName
    ? element
    : offsetParent(nodeName, element.parentElement)

const AccordionWrapper: FunctionComponent<AccordionWrapperProps> = (props: AccordionWrapperProps) => {
  const {
    setAccordionExpandedNodeId,
    setAccordionExpandedNodeTitle,
    setAccordionGroup,
    setAccordionUrlNode,
    setAccordionBreadCrumbClicked,
    group = RESPONSE_PENDING,
    expandCallback = noop,
    selectCallback = noop,
    eventCategory = 'Accordion',
    setAccordionState,
    highlight,
    sectionId,
    flavour,
    flavoured = false,
    updateExpandedNodeIdState,
    pageToggleRight,
    aol
  } = props

  useEffect(() => {
    const resetAccordion = (): void => {
      setAccordionExpandedNodeId('')
      setAccordionExpandedNodeTitle('')
      setAccordionGroup(RESPONSE_PENDING)
      setAccordionUrlNode('')
      setAccordionBreadCrumbClicked(false)
    }
    resetAccordion()

    return resetAccordion
  }, [])

  useEffect(() => {
    const accordionAol = typeof aol === 'string' ? aol : undefined // not sure why sometimes aol is []
    setAccordionState({ highlight, eventCategory, expandCallback, group, sectionId, aol: accordionAol })
  }, [highlight, eventCategory, expandCallback, group, sectionId])

  const { handleClick } = useClickEvents({ pageToggleRight })

  const handleChange = (evt: ChangeEvent<HTMLDivElement>): void => {
    const { action, type, path, value } = evt.target.dataset
    const callback: (...args: any[]) => any = (action === 'expand') ? expandCallback : selectCallback

    if (Boolean(callback(path, type, flavour, +(value ?? '0'))) && action === 'expand') {
      const { target } = evt
      const parentEl = offsetParent('li', target)
      if (parentEl !== null) updateExpandedNodeIdState(parentEl.id, value ?? '')
    }
  }

  const className = cx({ flavoured: !!flavoured })

  return (
    <div
      className={className}
      onClick={handleClick}
      onChange={handleChange}
    >
      <ResponseConsumer
        response={group}
        skeleton='qa-accordion'
        notfoundComponent={() => <QaAccordionNotFoundContainer {...props} />}
        content={() => !isEmpty(group)
          ? <AccordionFoundContainer {...props} />
          : <QaAccordionNotFoundContainer {...props} />}
      />
    </div>
  )
}

interface AccordionWrapperBase extends ExpandCallback, SelectCallback {
  aol: string | string[]
  flavoured: boolean
  contained: boolean
  SectionHeaderOverride: JSXElementConstructor<unknown>
  section: string
  cxNames: any // FIX ME
  showAllS1: boolean
  copyright: string
  expandCallback: (...args: any) => any
  selectCallback: (...args: any) => any
  legacyClassWrapper?: boolean
}

export type AccordionWrapperProps = AccordionWrapperContainerProps & AccordionState & AccordionWrapperBase

export default AccordionWrapper
